import { useTranslation } from 'react-i18next';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';
import { SIMERA_FEE } from '$constants/SimeraOperations';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import capitalizedName from '$utils/capitalizedName';
import computeYearsOfExperience from '$utils/computeYearsOfExperience';
import { ReactComponent as Star } from '$icons/uncolored/star.svg';
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { GetSharedCandidatesRosterDataOutput } from '$store/modules/candidates';
import { countriesCodes } from '$constants/countriesCodes';
import { ReactComponent as Login } from '$icons/uncolored/login.svg';
import SPKCandidateVideos from '../SPKCandidateVideos/SPKCandidateVideos';
import SPKCandidateCardSkill from './SPKCandidateSkills';
import SPKCandidateHiddenSkills from './SPKCandidateHiddenSkills';

const SPKCandidateCard = ({
  candidate,
  showSidebarHandler,
  requestInterviewHandler,
  feeAmount,
}: {
  candidate: GetSharedCandidatesRosterDataOutput['candidates'][number];
  showSidebarHandler: (
    candidate: GetSharedCandidatesRosterDataOutput['candidates'][number],
    showSmartScore?: boolean
  ) => void;
  requestInterviewHandler: () => void;
  feeAmount?: number;
}) => {
  const [t] = useTranslation('global');
  const { currency, currencyCode, usdToCurrencyConverter } = useAppSelector(
    (state) => state.brand
  );
  const [overflowStart, setOverflowStart] = useState<number>();
  const [hiddenSkills, setHiddenSkills] = useState<
    GetSharedCandidatesRosterDataOutput['candidates'][number]['CandidateSkills']
  >([]);
  const [visibleSkills, setVisibleSkills] = useState<
    GetSharedCandidatesRosterDataOutput['candidates'][number]['CandidateSkills']
  >([]);

  const skillsContainerRef = useRef<HTMLDivElement | null>(null);
  const videoParentRef = useRef<HTMLDivElement | null>(null);

  const yearsOfExperience = useMemo(() => {
    return computeYearsOfExperience(candidate.CandidateWorkExperience);
  }, [candidate.CandidateWorkExperience]);

  const onOverflowHandler = (index: number) => {
    setOverflowStart(index);
  };

  const handleClickCard = (
    candidateData: GetSharedCandidatesRosterDataOutput['candidates'][number],
    showSmartScore = false
  ) => {
    showSidebarHandler(candidateData, showSmartScore);
  };

  const countryEmoji = useMemo(() => {
    return countriesCodes.find(
      (singleCountry) =>
        singleCountry.name.toLowerCase() ===
        candidate?.ResidenceCountry?.toLowerCase()
    )?.code;
  }, [candidate?.ResidenceCountry]);

  useEffect(() => {
    if (!candidate.CandidateSkills) return;

    if (!overflowStart && candidate.CandidateSkills) {
      setVisibleSkills(candidate.CandidateSkills);
      return;
    }

    const visible = candidate.CandidateSkills.slice(0, overflowStart);
    const hidden = candidate.CandidateSkills.slice(overflowStart);

    setVisibleSkills(visible);
    setHiddenSkills(hidden);
  }, [candidate.CandidateSkills, overflowStart]);

  return (
    <div
      data-testid="spkCandidateCard"
      role="button"
      tabIndex={0}
      onKeyDown={() => {}}
      onClick={(ev: MouseEvent<HTMLDivElement>) => {
        if ((ev.target as HTMLElement).nodeName.toLowerCase() !== 'video')
          handleClickCard(candidate);
      }}
      className={`flex flex-col md:flex-row shadow md:shadow-none rounded-2xl ${
        candidate.Highlight && 'border-[3px] border-employer-primary'
      }`}
    >
      <div className="bg-white rounded-2xl flex flex-row p-2 sm:p-4 divide-x-2 divide-gray-100 *:px-6 w-full">
        <div className="flex flex-col flex-1 w-2/4">
          {/* Name, last job and country */}
          <div className="flex flex-row gap-4 mb-8 items-center">
            <div className="w-10 min-w-10">
              <DefaultIcon2
                isEmployer
                firstName={candidate.User?.FirstName}
                lastName={candidate.User?.LastName}
                fontSize="text-xl"
                hideBorder
                picture={candidate?.User?.ProfilePicture?.Url}
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:divide-x divide-gray-400 *:px-4 -mx-4 sm:items-center">
              <div className="flex font-bold text-base">
                {`${capitalizedName(
                  candidate.User?.FirstName
                )} ${capitalizedName(candidate.User?.LastName)}.`}
              </div>
              <div className="font-bold flex flex-col sm:flex-row">
                <span>{candidate?.CandidateWorkExperience?.[0]?.JobTitle}</span>
                {candidate.ResidenceCountry && (
                  <span className="text-nowrap font-light text-xs border border-neutral-200 text-neutral-600 rounded-md px-2 shadow flex gap-2 items-center self-start sm:self-center ml-4">
                    <span className={`fi fi-${countryEmoji?.toLowerCase()}`} />
                    {candidate.ResidenceCountry}
                  </span>
                )}
              </div>
              {candidate.Highlight && (
                <div className="text-xs font-bold">
                  <span className="flex items-center gap-2 px-1.5 py-1 bg-employer-primary text-white rounded-md">
                    <Star fill="white" width="16" height="16" />
                    {t('PositionCard.Highlight')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* General Info */}
          <div className="text-sm mb-2">
            <div className="flex flex-row divide-x divide-gray-400 *:px-2 -mx-2">
              {candidate?.CandidateWorkExperience?.[0]?.Company && (
                <span className="flex flex-wrap text-black/50">
                  Highlight:{' '}
                  <span className="text-black ml-1">
                    {candidate?.CandidateWorkExperience?.[0]?.Company}
                  </span>
                </span>
              )}
              <span>
                <span className="text-black/50">Exp:</span> {yearsOfExperience}+
                yrs
              </span>
              <span>
                <span className="text-black/50">
                  Monthly Salary ({currencyCode}):
                </span>{' '}
                {currency}
                {usdToCurrencyConverter(
                  (candidate?.CandidateJobInterest?.SalaryLowerBound || 0) +
                    (feeAmount ?? SIMERA_FEE)
                ).toLocaleString('en-US', { style: 'decimal' })}
                /m {currencyCode}
              </span>
            </div>
          </div>
          {/* Skills */}
          <div className="flex flex-row items-center gap-2 mb-4">
            <div
              className="flex overflow-x-hidden gap-2 text-xs font-semibold text-gray-500 max-w-full"
              ref={skillsContainerRef}
            >
              {visibleSkills &&
                visibleSkills
                  .slice(0, overflowStart)
                  .map((singleSkill, index) => {
                    return (
                      <SPKCandidateCardSkill
                        key={`${singleSkill.Skill.title}_${singleSkill.Id}`}
                        skill={singleSkill.Skill.title}
                        index={index}
                        containerRef={
                          skillsContainerRef.current as HTMLDivElement
                        }
                        setOverflowIndex={onOverflowHandler}
                        isVerified={singleSkill.IsVerified}
                      />
                    );
                  })}
            </div>
            {hiddenSkills && hiddenSkills.length > 0 && (
              <SPKCandidateHiddenSkills
                hiddenSkills={hiddenSkills}
                uniqueId={candidate.Id}
              />
            )}
          </div>
          <div className="flex flex-row mt-auto text-sm divide-x divide-neutral-400/50 *:px-4 -mx-4 font-light">
            <button type="button" className="flex items-center group">
              <span className="group-hover:underline">
                {t('sharedPosition.sharedCandidatesRoster.fullProfile')}
              </span>
              <i className="pi pi-eye ml-2 text-employer-primary" />
            </button>
            <button
              type="button"
              className="flex items-center group gap-2"
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                requestInterviewHandler();
              }}
            >
              <span className="group-hover:underline">
                {t('sharedPosition.requestInterview')}
              </span>
              <Login className="stroke-employer-primary" />
            </button>
          </div>
        </div>
        {candidate.WorkSummary && (
          <div className="h-full w-1/4">
            <div className="h-full bg-spk-neutral-20 border p-4 border-solid border-spk-neutral-40 text-xs flex flex-col rounded-lg text-spk-mediumContrast">
              <p className="font-semibold">Summary:</p>
              <p className="line-clamp-[8]">{candidate.WorkSummary}</p>
            </div>
          </div>
        )}

        {candidate.AdditionalVideos &&
          candidate.AdditionalVideos.length > 0 && (
            <div
              id={`candidate_${candidate.Id}_videos`}
              className="flex-0 ml-auto w-1/4"
              ref={videoParentRef}
            >
              <div className="max-w-72 m-auto">
                <SPKCandidateVideos videos={candidate.AdditionalVideos} />
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

SPKCandidateCard.defaultProps = {
  feeAmount: SIMERA_FEE,
};

export default SPKCandidateCard;
